import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, ProgressBar, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import SparkMD5 from 'spark-md5';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://wfshare.wishfin.com/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const STATUS = {
  PENDING: 'pending',
  CALCULATING_HASH: 'calculating_hash',
  INITIALIZING: 'initializing',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ERROR: 'error'
};

const ResumableUpload = ({ 
  file, 
  folder = '', 
  onComplete, 
  onError, 
  onProgress, 
  autoStart = true,
  showControls = true
}) => {
  const { user } = useAuth();
  const [uploadId, setUploadId] = useState(null);
  const [uploadParts, setUploadParts] = useState([]);
  const [currentPart, setCurrentPart] = useState(0);
  const [status, setStatus] = useState(STATUS.PENDING);
  const [progress, setProgress] = useState(0);
  const [uploadedSize, setUploadedSize] = useState(0);
  const [error, setError] = useState('');
  const [partsCompleted, setPartsCompleted] = useState([]);
  const [partETags, setPartETags] = useState([]);
  const [filePath, setFilePath] = useState('');
  const [storageAvailable, setStorageAvailable] = useState(true);
  
  const fileRef = useRef(file);
  const abortControllerRef = useRef(null);

  // Check if local storage is available
  useEffect(() => {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      setStorageAvailable(true);
    } catch (e) {
      setStorageAvailable(false);
    }
  }, []);

  // Initialize upload when file changes
  useEffect(() => {
    if (!file) return;
    
    fileRef.current = file;
    
    if (autoStart) {
      checkResumable();
    }
  }, [file, autoStart]);

  // Report progress to parent
  useEffect(() => {
    if (onProgress) {
      onProgress(progress);
    }
  }, [progress, onProgress]);

  // Check if we can resume an existing upload
  const checkResumable = useCallback(async () => {
    if (!file) return;
    
    setStatus(STATUS.CALCULATING_HASH);
    setProgress(0);
    
    try {
      // Normalize folder path
      let normalizedFolder = folder || '';
      if (normalizedFolder.startsWith('/')) {
        normalizedFolder = normalizedFolder.slice(1);
      }
      if (normalizedFolder && !normalizedFolder.endsWith('/')) {
        normalizedFolder += '/';
      }
      
      // Calculate the file path
      const path = `${normalizedFolder}${file.name}`;
      setFilePath(path);
      
      // Check if there's a resumable upload
      const response = await axios.get(
        `${API_URL}/multipart/resume-info`,
        {
          params: { 
            file_path: path,
            file_size: file.size,
            user_id: user?.id
          },
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      if (response.data.can_resume) {
        // Resume existing upload
        setUploadId(response.data.upload_id);
        
        // Mark parts as completed
        const completedParts = response.data.uploaded_parts.map(part => ({
          PartNumber: part.PartNumber,
          ETag: part.ETag
        }));
        
        setPartETags(completedParts);
        setPartsCompleted(completedParts.map(p => p.PartNumber));
        
        // Recalculate progress
        const uploadedBytes = completedParts.reduce((total, part) => total + part.Size, 0);
        setUploadedSize(uploadedBytes);
        setProgress(Math.round((uploadedBytes / file.size) * 100));
        
        // Initialize new upload with existing upload ID
        await initializeUpload(response.data.upload_id);
      } else {
        // Start fresh upload
        initializeNewUpload();
      }
    } catch (err) {
      console.error("Error checking resumable status:", err);
      // If we can't check resumable status, start fresh
      initializeNewUpload();
    }
  }, [file, folder, user?.id]);

  // Finalize upload when all parts are complete
  useEffect(() => {
    // Ensure we have parts and all of them are completed
    if (uploadId && uploadParts.length > 0 && partsCompleted.length === uploadParts.length) {
      // Ensure we are not already completed or in an error state
      if (status !== STATUS.COMPLETED && status !== STATUS.ERROR) {
        console.log('All parts uploaded, finalizing...');
        finalizeUpload();
      }
    }
  }, [partsCompleted, uploadParts, uploadId, status]);

  // Initialize a new upload
  const initializeNewUpload = useCallback(async () => {
    if (!file) return;
    
    setStatus(STATUS.INITIALIZING);
    
    try {
      // Normalize folder path
      let normalizedFolder = folder || '';
      if (normalizedFolder.startsWith('/')) {
        normalizedFolder = normalizedFolder.slice(1);
      }
      
      const response = await axios.post(
        `${API_URL}/multipart/init`,
        {
          file_name: file.name,
          file_size: file.size,
          content_type: file.type || 'application/octet-stream',
          folder: normalizedFolder
        },
        {
          params: { user_id: user?.id },
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setUploadId(response.data.upload_id);
      setUploadParts(response.data.parts);
      setFilePath(response.data.file_path);
      
      // Save the upload ID to local storage for recovery
      if (storageAvailable) {
        const storageKey = `resumable_upload_${response.data.file_path}`;
        localStorage.setItem(storageKey, JSON.stringify({
          uploadId: response.data.upload_id,
          timestamp: Date.now()
        }));
      }
      
      // Start uploading
      setStatus(STATUS.UPLOADING);
      uploadPart(0, response.data.upload_id, response.data.parts);
    } catch (err) {
      console.error("Error initializing upload:", err);
      setError(err.response?.data?.detail || 'Failed to initialize upload');
      setStatus(STATUS.ERROR);
      if (onError) onError(err);
    }
  }, [file, folder, user?.id, storageAvailable]);

  // Initialize with an existing upload ID
  const initializeUpload = useCallback(async (existingUploadId) => {
    if (!file) return;
    
    setStatus(STATUS.INITIALIZING);
    
    try {
      // Get the status of the multipart upload
      const statusResponse = await axios.get(
        `${API_URL}/multipart/status`,
        {
          params: { 
            upload_id: existingUploadId,
            user_id: user?.id
          },
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      // Calculate chunk size based on file size and total parts
      const chunkSize = Math.ceil(file.size / statusResponse.data.total_parts);
      
      // Re-create the parts array
      const parts = [];
      for (let i = 0; i < statusResponse.data.total_parts; i++) {
        parts.push({
          part_number: i + 1,
          start_byte: i * chunkSize,
          end_byte: Math.min((i + 1) * chunkSize - 1, file.size - 1)
        });
      }
      
      setUploadParts(parts);
      
      // Find the first part that hasn't been completed
      const nextPartIndex = parts.findIndex(part => 
        !partsCompleted.includes(part.part_number)
      );
      
      if (nextPartIndex === -1) {
        // All parts completed, finalize the upload
        await finalizeUpload();
      } else {
        // Start uploading from the next part
        setStatus(STATUS.UPLOADING);
        uploadPart(nextPartIndex, existingUploadId, parts);
      }
    } catch (err) {
      console.error("Error initializing existing upload:", err);
      // If we can't resume, start fresh
      initializeNewUpload();
    }
  }, [file, partsCompleted, user?.id, initializeNewUpload]);

  // Upload a single part
  const uploadPart = useCallback(async (partIndex, currentUploadId, parts) => {
    console.log(`Starting upload of part ${partIndex + 1} of ${parts.length}...`);
    if (!file || partIndex >= parts.length) return;
    
    // Skip if this part is already completed
    if (partsCompleted.includes(parts[partIndex].part_number)) {
      uploadPart(partIndex + 1, currentUploadId, parts);
      return;
    }
    
    try {
      setCurrentPart(partIndex);
      
      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();
      
      // Prepare the part data
      const start = parts[partIndex].start_byte;
      const end = parts[partIndex].end_byte;
      const chunk = file.slice(start, end + 1);
      
      // Create FormData for part upload
      const formData = new FormData();
      formData.append('upload_id', currentUploadId);
      formData.append('part_number', parts[partIndex].part_number);
      
      // Create a Blob from the chunk and append it as a file
      const chunkBlob = new Blob([chunk], { type: file.type || 'application/octet-stream' });
      formData.append('file_part', chunkBlob, 'part');
      
      // Calculate the weight of this part in the overall progress
      const partSize = end - start + 1;
      const partWeight = partSize / file.size;
      
      // Pre-calculate the completed parts contribution to overall progress
      const completedPartsPercentage = (uploadedSize / file.size) * 100;
      
      // Get part number for tracking
      const currentPartNumber = parts[partIndex].part_number;
      const totalParts = parts.length;
      
      console.log(`Starting upload of part ${currentPartNumber}/${totalParts} - Size: ${(partSize / (1024 * 1024)).toFixed(2)}MB (${(partWeight * 100).toFixed(2)}% of total)`);
      
      // Upload the part through the backend API
      const uploadResponse = await axios.post(
        `${API_URL}/multipart/upload-part`,
        formData,
        {
          params: { user_id: user?.id },
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            // Calculate part upload progress (0 to 1)
            const partProgress = progressEvent.loaded / progressEvent.total;
            
            // Scale the part progress to the overall file progress
            // This part's contribution is its size relative to the whole file times its upload progress
            const partContribution = partWeight * partProgress;
            
            // Calculate this part's current contribution in percentage points
            const thisPartPercentage = partContribution * 100;
            
            // Calculate total progress including all completed parts and this part's current progress
            const totalProgressPercentage = completedPartsPercentage + thisPartPercentage;
            
            // Set the progress (capped at 99% until final completion)
            const newProgress = Math.min(99, Math.round(totalProgressPercentage));
            setProgress(newProgress);
            
            // Verbose logging during upload (uncomment for debugging)
            // console.log(`Part ${currentPartNumber}/${totalParts} progress: ${(partProgress * 100).toFixed(1)}%, Total: ${totalProgressPercentage.toFixed(1)}%`);
          },
          signal: abortControllerRef.current.signal
        }
      );
      
      console.log(`Part ${parts[partIndex].part_number} uploaded successfully, continuing to next part...`);
      
      // Get the ETag from the response
      const etag = uploadResponse.data.etag;
      
      // Mark part as completed
      setPartsCompleted(prev => [...prev, parts[partIndex].part_number]);
      setPartETags(prev => [
        ...prev, 
        { 
          PartNumber: parts[partIndex].part_number, 
          ETag: etag.replace(/"/g, '') // Remove quotes from ETag
        }
      ]);
      
      // Calculate the size of the part we just uploaded
      const bytesUploadedThisPart = end - start + 1;

      // Calculate total bytes uploaded by using the end_byte of the current part.
      // This avoids relying on stale state from the 'uploadedSize' variable in the recursive calls.
      const totalBytesUploaded = parts[partIndex].end_byte + 1;

      // Calculate what percentage of the file this part represents
      const partPercentage = (bytesUploadedThisPart / file.size) * 100;
      
      // Calculate what percentage of the file we've uploaded in total
      const totalPercentage = (totalBytesUploaded / file.size) * 100;
      
      // Determine the part number for logging
      const partNumber = parts[partIndex].part_number;
      
      // Log detailed progress information
      console.log(`Part ${partNumber}/${parts.length} complete. This part: ${partPercentage.toFixed(2)}%, Total progress: ${totalPercentage.toFixed(2)}%`);
      
      // Update the state with the new data
      setUploadedSize(totalBytesUploaded);
      
      // Cap progress at 99% until final completion
      const newProgress = Math.min(99, Math.round(totalPercentage));
      setProgress(newProgress);
      
      console.log(`Progress updated to ${newProgress}% after completing part ${partNumber}/${parts.length}`);
      
      // No need to notify backend separately that part is uploaded
      // The upload-part endpoint already updates the database
      
      // If there are more parts to upload, continue with the next one.
      // The finalization will be triggered by the useEffect hook that
      // watches for all parts to be completed.
      if (partIndex < parts.length - 1) {
        uploadPart(partIndex + 1, currentUploadId, parts);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Upload was cancelled, set status to paused
        setStatus(STATUS.PAUSED);
      } else {
        console.error(`Error uploading part ${partIndex + 1}:`, err);
        setError(`Failed to upload part ${partIndex + 1}: ${err.message}`);
        setStatus(STATUS.ERROR);
        if (onError) onError(err);
      }
    }
  }, [file, uploadedSize, filePath, partsCompleted, user?.id, onError]);

  // Finalize the multipart upload
  const finalizeUpload = useCallback(async () => {
    if (!uploadId || !file) return;
    
    try {
      // Create form data for the complete request
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('file_path', filePath);
      formData.append('parts', JSON.stringify(partETags));
      
      // Complete the multipart upload
      await axios.post(
        `${API_URL}/multipart/complete`,
        formData,
        {
          params: { user_id: user?.id },
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Clear the stored upload ID
      if (storageAvailable) {
        const storageKey = `resumable_upload_${filePath}`;
        localStorage.removeItem(storageKey);
      }
      
      // Set final status and progress
      console.log('Upload complete! Setting final progress to 100%');
      setStatus(STATUS.COMPLETED);
      setProgress(100);
      
      // Calculate total upload size in MB
      const totalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      console.log(`Successfully uploaded file: ${file.name} (${totalSizeMB} MB)`);
      
      if (onComplete) {
        onComplete({ filePath, fileName: file.name, size: file.size });
      }
    } catch (err) {
      console.error("Error completing upload:", err);
      setError(err.response?.data?.detail || 'Failed to complete upload');
      setStatus(STATUS.ERROR);
      if (onError) onError(err);
    }
  }, [uploadId, file, filePath, partETags, user?.id, storageAvailable, onComplete, onError]);

  // Pause the upload
  const pauseUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStatus(STATUS.PAUSED);
  };

  // Resume the upload
  const resumeUpload = () => {
    if (status === STATUS.PAUSED && uploadId) {
      setStatus(STATUS.UPLOADING);
      uploadPart(currentPart, uploadId, uploadParts);
    }
  };

  // Abort the upload completely
  const abortUpload = async () => {
    if (!uploadId) return;
    
    try {
      // Abort the multipart upload
      const formData = new FormData();
      formData.append('upload_id', uploadId);
      formData.append('file_path', filePath);
      
      await axios.post(
        `${API_URL}/multipart/abort`,
        formData,
        {
          params: { user_id: user?.id },
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Clear the stored upload ID
      if (storageAvailable) {
        const storageKey = `resumable_upload_${filePath}`;
        localStorage.removeItem(storageKey);
      }
      
      // Reset state
      setUploadId(null);
      setUploadParts([]);
      setCurrentPart(0);
      setStatus(STATUS.PENDING);
      setProgress(0);
      setUploadedSize(0);
      setPartsCompleted([]);
      setPartETags([]);
      setError('');
      
    } catch (err) {
      console.error("Error aborting upload:", err);
      setError(err.response?.data?.detail || 'Failed to abort upload');
    }
  };

  // Restart the upload from scratch
  const restartUpload = () => {
    abortUpload().then(() => {
      initializeNewUpload();
    });
  };

  // Calculate upload speed (bytes per second)
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const lastTimeRef = useRef(Date.now());
  const lastUploadedRef = useRef(0);

  useEffect(() => {
    if (status === STATUS.UPLOADING) {
      const now = Date.now();
      const timeDiff = now - lastTimeRef.current;
      
      if (timeDiff > 1000) { // Update every second
        const bytesDiff = uploadedSize - lastUploadedRef.current;
        const speedBps = bytesDiff / (timeDiff / 1000);
        
        setUploadSpeed(speedBps);
        
        // Calculate time remaining
        const bytesRemaining = file ? file.size - uploadedSize : 0;
        if (speedBps > 0) {
          const secondsRemaining = Math.round(bytesRemaining / speedBps);
          setTimeRemaining(secondsRemaining);
        }
        
        lastTimeRef.current = now;
        lastUploadedRef.current = uploadedSize;
      }
    } else {
      setUploadSpeed(0);
      setTimeRemaining(null);
    }
  }, [uploadedSize, status, file]);

  // Format bytes to human readable format
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Format time remaining to human readable format
  const formatTimeRemaining = (seconds) => {
    if (!seconds) return 'Calculating...';
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s remaining`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s remaining`;
    } else {
      return `${secs}s remaining`;
    }
  };

  // Status labels
  const getStatusLabel = () => {
    switch (status) {
      case STATUS.CALCULATING_HASH:
        return 'Calculating file hash...';
      case STATUS.INITIALIZING:
        return 'Initializing upload...';
      case STATUS.UPLOADING:
        return 'Uploading...';
      case STATUS.PAUSED:
        return 'Upload paused';
      case STATUS.COMPLETED:
        return 'Upload complete';
      case STATUS.ERROR:
        return 'Upload error';
      default:
        return 'Ready to upload';
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>
          {file?.name || 'No file selected'} 
          {file && <span className="text-muted"> ({formatBytes(file.size)})</span>}
        </Card.Title>
        
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        <ProgressBar 
          now={progress} 
          variant={status === STATUS.ERROR ? 'danger' : 
                 status === STATUS.COMPLETED ? 'success' : 'primary'}
          label={`${progress}%`}
          className="mb-2"
        />
        
        <Row className="mb-3">
          <Col>
            <Badge bg="info">{getStatusLabel()}</Badge>
          </Col>
          <Col className="text-end">
            {status === STATUS.UPLOADING && (
              <>
                <span className="me-3">{formatBytes(uploadSpeed)}/s</span>
                <span>{timeRemaining && formatTimeRemaining(timeRemaining)}</span>
              </>
            )}
            {status === STATUS.COMPLETED && (
              <Badge bg="success">Completed</Badge>
            )}
          </Col>
        </Row>
        
        {showControls && (
          <div className="d-flex">
            {status === STATUS.UPLOADING && (
              <Button variant="warning" onClick={pauseUpload} className="me-2">
                Pause
              </Button>
            )}
            
            {status === STATUS.PAUSED && (
              <Button variant="primary" onClick={resumeUpload} className="me-2">
                Resume
              </Button>
            )}
            
            {(status === STATUS.PAUSED || status === STATUS.ERROR) && (
              <Button variant="danger" onClick={abortUpload} className="me-2">
                Cancel Upload
              </Button>
            )}
            
            {status === STATUS.ERROR && (
              <Button variant="primary" onClick={restartUpload} className="me-2">
                Restart Upload
              </Button>
            )}
            
            {status === STATUS.PENDING && (
              <Button variant="primary" onClick={checkResumable}>
                Start Upload
              </Button>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ResumableUpload;